import { useState } from "react";
import { logger } from "@/utils/logger";
import { motion } from "framer-motion";
import { Home, RotateCcw, Volume2, VolumeX } from "lucide-react";
import { Button } from "@/components/ui/button";
import Confetti from "react-confetti";
import { useWindowSize } from "@/hooks/useWindowSize";
import { savePlayerNames, loadPlayerNames } from "@/utils/playerStorage";
import { recordGameWon } from "@/utils/playerProfile";

import { getVoiceLang } from "@/utils/voicePref";
const ANIMALS = [
  { emoji: "🐰", name: "Kelinci", color: "#FFB6C1" },
  { emoji: "🐢", name: "Kura-kura", color: "#90EE90" },
  { emoji: "🐶", name: "Anjing", color: "#DEB887" },
  { emoji: "🐱", name: "Kucing", color: "#FFD700" },
  { emoji: "🐸", name: "Kodok", color: "#32CD32" },
  { emoji: "🦊", name: "Rubah", color: "#FF8C00" },
];

const speakText = (t) => {
  try { if (!window.speechSynthesis) return; window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(t); u.lang = getVoiceLang(); u.rate=0.85; u.pitch=1.0; window.speechSynthesis.speak(u);
  } catch (err) { logger.debug("game op failed", err); }
};

const LombaLari = ({ onBackToMenu }) => {
  const { width: winW, height: winH } = useWindowSize();
  const [gameState, setGameState] = useState("menu");
  const [playerNames, setPlayerNames] = useState(() => { const s = loadPlayerNames(); return [s[0]||"",s[1]||"",s[2]||"",s[3]||""]; });
  const [playerCount, setPlayerCount] = useState(2);
  const [animalChoices, setAnimalChoices] = useState([0, 1, 2, 3]);
  const [positions, setPositions] = useState([0, 0, 0, 0]);
  const [winner, setWinner] = useState(null);
  const [soundOn, setSoundOn] = useState(true);
  const [tapping, setTapping] = useState([false, false, false, false]);

  const finishLine = 100;
  const names = playerNames.map((n, i) => n || `Pemain ${i + 1}`);

  const startGame = () => {
    savePlayerNames(playerNames);
    setPositions([0, 0, 0, 0]);
    setWinner(null);
    setGameState("playing");
    if (soundOn) speakText("Siap! Start!");
  };

  const tapPlayer = (idx) => {
    if (gameState !== "playing" || winner) return;
    const boost = 3 + Math.random() * 4;
    setPositions(prev => {
      const next = [...prev];
      next[idx] = Math.min(finishLine, next[idx] + boost);
      if (next[idx] >= finishLine && !winner) {
        setWinner(idx);
        recordGameWon("lomba");
        if (soundOn) speakText(`${names[idx]} wins! ${ANIMALS[animalChoices[idx]].name} is fastest!`);
      }
      return next;
    });
    setTapping(prev => { const n = [...prev]; n[idx] = true; return n; });
    setTimeout(() => setTapping(prev => { const n = [...prev]; n[idx] = false; return n; }), 100);
  };

  // Keyboard
  const keys = ['a', 'j', 'f', 'k'];

  if (typeof window !== 'undefined' && gameState === "playing") {
    window.onkeydown = (e) => {
      const idx = keys.indexOf(e.key.toLowerCase());
      if (idx >= 0 && idx < playerCount) tapPlayer(idx);
    };
  }

  if (gameState === "menu") {
    return (
      <div className="flex flex-col items-center w-full max-w-2xl mx-auto px-4 pt-4" data-testid="lomba-lari-menu">
        <motion.div initial={{ y: -20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} className="glass-card-strong rounded-2xl p-6 w-full text-center mb-4">
          <motion.span className="text-6xl block mb-3" animate={{ x: [0, 10, 0] }} transition={{ duration: 1, repeat: Infinity }}>🏃</motion.span>
          <h1 className="font-fredoka text-3xl text-gold font-bold mb-2">Lomba Lari Hewan</h1>
          <p className="font-fredoka text-gold-light/80 text-sm mb-3">Tap cepat-cepat untuk lari ke finish!</p>

          <div className="flex gap-2 mb-3 justify-center">
            {[2, 3, 4].map(n => (
              <Button key={n} onClick={() => setPlayerCount(n)}
                className={`font-fredoka text-xs rounded-lg ${playerCount === n ? 'bg-gold text-brown-dark' : 'glass-card text-gold'}`}>
                {n} Pemain
              </Button>
            ))}
          </div>

          <div className="grid grid-cols-2 gap-2 mb-3">
            {Array.from({ length: playerCount }).map((_, i) => (
              <div key={i}>
                <div className="flex items-center gap-1 mb-1">
                  <span className="text-lg">{ANIMALS[animalChoices[i]].emoji}</span>
                  <select value={animalChoices[i]} onChange={e => { const n = [...animalChoices]; n[i] = parseInt(e.target.value); setAnimalChoices(n); }}
                    className="glass-card border-gold/20 rounded px-1 py-0.5 text-white font-fredoka text-xs bg-transparent">
                    {ANIMALS.map((a, ai) => <option key={ai} value={ai}>{a.name}</option>)}
                  </select>
                </div>
                <input type="text" value={playerNames[i]} onChange={e => { const n = [...playerNames]; n[i] = e.target.value; setPlayerNames(n); }}
                  placeholder={`Pemain ${i+1}`} maxLength={12}
                  className="w-full glass-card border-gold/20 rounded-xl px-2 py-1.5 text-white font-fredoka text-xs focus:outline-none" />
              </div>
            ))}
          </div>

          <Button onClick={startGame} className="w-full h-12 font-fredoka font-bold text-lg rounded-xl bg-gradient-to-r from-green-400 to-emerald-500" data-testid="lomba-start">MULAI LOMBA!</Button>
        </motion.div>
        <Button onClick={onBackToMenu} variant="outline" className="w-full glass-card border-gold/20 text-gold hover:bg-gold/10 font-fredoka rounded-xl"><Home className="w-4 h-4 mr-2" />Menu</Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center w-full max-w-3xl mx-auto px-2" data-testid="lomba-lari-game">
      {winner !== null && <Confetti width={winW} height={winH} recycle={false} numberOfPieces={300} />}
      <div className="w-full glass-card-strong rounded-xl p-2 mb-2 flex justify-between items-center">
        <span className="font-fredoka text-gold font-bold text-sm">🏁 Lomba Lari Hewan</span>
        <div className="flex gap-1.5">
          <Button onClick={() => setSoundOn(!soundOn)} variant="outline" size="sm" className="glass-card border-gold/20 text-gold h-7 w-7 p-0">{soundOn ? <Volume2 className="w-3 h-3" /> : <VolumeX className="w-3 h-3" />}</Button>
          <Button onClick={() => setGameState("menu")} variant="outline" size="sm" className="glass-card border-gold/20 text-gold h-7 px-2"><Home className="w-3 h-3" /></Button>
        </div>
      </div>

      {/* Race tracks */}
      <div className="w-full rounded-2xl overflow-hidden p-3 sm:p-4" style={{ background: 'linear-gradient(180deg, #87CEEB 0%, #90EE90 100%)' }}>
        {Array.from({ length: playerCount }).map((_, i) => (
          <div key={i} className="relative mb-3 last:mb-0">
            <div className="h-14 sm:h-16 rounded-xl relative overflow-hidden" style={{ background: `${ANIMALS[animalChoices[i]].color}30`, border: `2px solid ${ANIMALS[animalChoices[i]].color}50` }}>
              {/* Track lines */}
              <div className="absolute inset-0 flex items-center">
                <div className="w-full h-1 bg-white/20" />
              </div>
              {/* Finish line */}
              <div className="absolute right-2 top-0 bottom-0 w-1 bg-red-500/50" style={{ backgroundImage: 'repeating-linear-gradient(0deg, #000 0px, #000 4px, #fff 4px, #fff 8px)' }} />
              {/* Animal */}
              <motion.div className="absolute top-1/2 -translate-y-1/2 text-3xl sm:text-4xl z-10"
                style={{ left: `${positions[i]}%`, transform: `translateX(-50%) translateY(-50%)` }}
                animate={tapping[i] ? { y: [-2, -10, -2], rotate: [0, -5, 5, 0] } : {}}>
                {ANIMALS[animalChoices[i]].emoji}
              </motion.div>
              {/* Name */}
              <span className="absolute left-2 top-1 font-fredoka text-xs font-bold opacity-60">{names[i]} ({keys[i].toUpperCase()})</span>
              {/* Winner badge */}
              {winner === i && <span className="absolute right-4 top-1/2 -translate-y-1/2 text-2xl">🏆</span>}
            </div>
          </div>
        ))}
        {/* Finish flag */}
        <div className="text-right pr-2 mt-1">
          <span className="font-fredoka text-xs text-gray-600 font-bold">🏁 FINISH</span>
        </div>
      </div>

      {/* Winner overlay */}
      {winner !== null && (
        <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} className="mt-4 glass-card-strong rounded-2xl p-6 text-center w-full max-w-md">
          <span className="text-5xl block mb-2">{ANIMALS[animalChoices[winner]].emoji}</span>
          <h2 className="font-fredoka text-2xl text-gold font-bold mb-1">{names[winner]} Menang!</h2>
          <p className="font-fredoka text-gold-light/80 text-sm mb-3">{ANIMALS[animalChoices[winner]].name} tercepat!</p>
          <div className="flex gap-2">
            <Button onClick={startGame} className="flex-1 font-fredoka font-bold rounded-xl bg-gradient-to-r from-yellow-400 to-amber-500"><RotateCcw className="w-4 h-4 mr-2" />Lagi</Button>
            <Button onClick={() => setGameState("menu")} variant="outline" className="flex-1 glass-card border-gold/20 text-gold font-fredoka rounded-xl">Menu</Button>
          </div>
        </motion.div>
      )}

      {/* Tap buttons */}
      {!winner && (
        <div className={`grid gap-2 w-full mt-3 ${playerCount <= 2 ? 'grid-cols-2' : 'grid-cols-2 sm:grid-cols-4'}`}>
          {Array.from({ length: playerCount }).map((_, i) => (
            <Button key={i} data-testid={`lomba-tap-${i}`} onClick={() => tapPlayer(i)}
              className="h-16 font-fredoka font-bold text-sm rounded-2xl btn-3d"
              style={{ background: ANIMALS[animalChoices[i]].color, touchAction: "manipulation" }}>
              {ANIMALS[animalChoices[i]].emoji} {names[i]}
            </Button>
          ))}
        </div>
      )}
    </div>
  );
};

export default LombaLari;
